import * as React from "react"
import { cn } from "@/lib/utils"

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?:
        | "default"
        | "sovereign"
        | "ornate"
        | "outline"
        | "secondary"
        | "ghost"
        | "link"
        | "destructive"
        | "tactical"
        | "premium"
        | "glass"
    size?: "default" | "sm" | "lg" | "icon" | "xl"
    showFiligree?: boolean
}

/* ── Modern Architectural Corner Filigree (Subtle & Elegant) ── */
function CornerAccents({ color = "copper" }: { color?: "copper" | "bronze" | "gold" }) {
    const stroke =
        color === "gold"
            ? "#D8B478"
            : color === "bronze"
            ? "#8C6E4A"
            : "#C88246"

    return (
        <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]" aria-hidden="true">
            {/* Top-Left */}
            <svg className="absolute left-1 top-1 h-2 w-2 opacity-70 group-hover:opacity-100 transition-opacity" viewBox="0 0 8 8" fill="none">
                <path d="M1 7V2C1 1.44772 1.44772 1 2 1H7" stroke={stroke} strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            {/* Top-Right */}
            <svg className="absolute right-1 top-1 h-2 w-2 opacity-70 group-hover:opacity-100 transition-opacity" viewBox="0 0 8 8" fill="none">
                <path d="M7 7V2C7 1.44772 6.55228 1 6 1H1" stroke={stroke} strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            {/* Bottom-Left */}
            <svg className="absolute bottom-1 left-1 h-2 w-2 opacity-70 group-hover:opacity-100 transition-opacity" viewBox="0 0 8 8" fill="none">
                <path d="M1 1V6C1 6.55228 1.44772 7 2 7H7" stroke={stroke} strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            {/* Bottom-Right */}
            <svg className="absolute bottom-1 right-1 h-2 w-2 opacity-70 group-hover:opacity-100 transition-opacity" viewBox="0 0 8 8" fill="none">
                <path d="M7 1V6C7 6.55228 6.55228 7 6 7H1" stroke={stroke} strokeWidth="1.2" strokeLinecap="round" />
            </svg>
        </span>
    )
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant = "default",
            size = "default",
            showFiligree = false,
            children,
            ...props
        },
        ref
    ) => {
        const variants = {
            /* 1. Sovereign Crimson CTA (Primary Action) */
            sovereign:
                "group relative bg-gradient-to-b from-[#681812] via-[#3E0E0A] to-[#260806] hover:from-[#7D1D16] hover:via-[#4C120D] hover:to-[#320B08] text-[#FFF6EB] hover:text-white border border-[#C88246] hover:border-[#E8A263] shadow-[0_2px_10px_rgba(0,0,0,0.8),inset_0_1px_0_rgba(255,200,140,0.3),inset_0_0_14px_rgba(180,45,20,0.35)] hover:shadow-[0_4px_20px_rgba(180,50,20,0.4),inset_0_1px_0_rgba(255,225,160,0.45),inset_0_0_20px_rgba(220,70,30,0.4)] active:translate-y-[1px]",

            /* Default points to Sovereign Crimson for maximum brand impact */
            default:
                "group relative bg-gradient-to-b from-[#681812] via-[#3E0E0A] to-[#260806] hover:from-[#7D1D16] hover:via-[#4C120D] hover:to-[#320B08] text-[#FFF6EB] hover:text-white border border-[#C88246] hover:border-[#E8A263] shadow-[0_2px_10px_rgba(0,0,0,0.8),inset_0_1px_0_rgba(255,200,140,0.3),inset_0_0_14px_rgba(180,45,20,0.35)] hover:shadow-[0_4px_20px_rgba(180,50,20,0.4),inset_0_1px_0_rgba(255,225,160,0.45),inset_0_0_20px_rgba(220,70,30,0.4)] active:translate-y-[1px]",

            /* 2. Ornate Smoked Obsidian */
            ornate:
                "group relative bg-gradient-to-b from-[#241E1A] via-[#15120F] to-[#1B1713] hover:from-[#302822] hover:via-[#1C1713] hover:to-[#241E19] text-[#D8C9B5] hover:text-[#FFF5E3] border border-[#5C4A37] hover:border-[#B58E54] shadow-[0_2px_8px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(200,165,115,0.18)] hover:shadow-[0_2px_14px_rgba(181,142,84,0.25),inset_0_1px_0_rgba(235,195,140,0.3),inset_0_0_12px_rgba(181,142,84,0.15)] active:translate-y-[1px]",

            /* 3. Secondary Obsidian */
            secondary:
                "group relative bg-[#1A1613] hover:bg-[#26201B] text-[#D8C9B5] hover:text-[#FFF5E3] border border-[#4D3D2E] hover:border-[#8C6E4A] shadow-sm active:translate-y-[1px]",

            /* 4. Elegant Dark Outline */
            outline:
                "group relative bg-[#120F0D]/70 backdrop-blur-md hover:bg-[#201A16] text-[#D8C8B5] hover:text-white border border-[#544333] hover:border-[#B58E54] shadow-sm hover:shadow-[0_0_14px_rgba(181,142,84,0.2)] active:translate-y-[1px]",

            /* 5. Tactical Command Action (Claim / Toggle / Filter) */
            tactical:
                "group relative bg-[#151210] hover:bg-[#221C17] text-[#CFC3B0] hover:text-white border border-[#4A3B2C] hover:border-[#8C6D45] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.6)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.8),inset_0_0_8px_rgba(140,109,69,0.15)] active:translate-y-[1px]",

            /* 6. Molten Champagne Gold (High Tier / Featured) */
            premium:
                "group relative bg-gradient-to-b from-[#B88E4B] via-[#8C642B] to-[#5C3F16] hover:from-[#CAA258] hover:via-[#9E7333] hover:to-[#6E4B1B] text-[#FFF9EF] hover:text-white border border-[#E5BC70] hover:border-[#FFE29A] shadow-[0_2px_12px_rgba(184,142,75,0.35),inset_0_1px_0_rgba(255,240,190,0.5),inset_0_0_12px_rgba(184,142,75,0.3)] active:translate-y-[1px]",

            /* 7. Blood Red Destructive */
            destructive:
                "group relative bg-gradient-to-b from-[#5C1210] via-[#330806] to-[#450B09] hover:from-[#751613] text-[#FFD8D6] hover:text-white border border-[#B0332E] hover:border-[#DE443E] shadow-sm active:translate-y-[1px]",

            /* 8. Ghost */
            ghost:
                "group relative text-[#9A8B7A] hover:text-[#EDE3D4] hover:bg-[#1E1915]/80 border border-transparent hover:border-[#3E3227]",

            /* 9. Dark Fantasy Link */
            link:
                "text-[#D8A758] underline-offset-4 hover:underline hover:text-[#FFCA75] p-0 h-auto font-sans font-semibold normal-case tracking-normal",

            /* 10. Smoked Glass */
            glass:
                "group relative bg-[#14110F]/60 backdrop-blur-xl border border-[#4A3B2C]/70 hover:border-[#8C6D45] text-[#E0D3C1] hover:text-white hover:bg-[#1F1914]/80 shadow-md active:translate-y-[1px]",
        }

        const sizes = {
            default: "h-11 px-6 py-2.5 text-xs font-bold tracking-[0.12em]",
            sm: "h-8 px-3.5 py-1 text-[11px] font-bold tracking-[0.08em]",
            lg: "h-13 px-8 py-3 text-sm font-black tracking-[0.14em]",
            xl: "h-15 px-10 py-4 text-base font-black tracking-[0.16em]",
            icon: "h-10 w-10 p-0",
        }

        const filigreeColor =
            variant === "sovereign" || variant === "default"
                ? "copper"
                : variant === "premium"
                ? "gold"
                : "bronze"

        return (
            <button
                className={cn(
                    "inline-flex items-center justify-center whitespace-nowrap rounded-md transition-all duration-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#D8B478] disabled:pointer-events-none disabled:opacity-40 uppercase font-cinzel select-none cursor-pointer",
                    variants[variant],
                    sizes[size],
                    className
                )}
                ref={ref}
                {...props}
            >
                {showFiligree && <CornerAccents color={filigreeColor} />}
                <span className="relative z-10 flex items-center justify-center gap-2">
                    {children}
                </span>
            </button>
        )
    }
)
Button.displayName = "Button"

export { Button }
